import React, { Component, CSSProperties, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import {
    AgBytesAPI,
    apiUrl,
    FileImportAPI,
    FormattingHelpers,
    PreferenceAPI,
    RecAPI,
    UnitAPI,
} from "@ai360/core";
import { stampCropSelectable, useCropList } from "~/utils/crops/use-crop-list";
import {
    isCropPurposeSelectDisabled,
    stampCropPurposeSelectable,
    useCropPurposeList,
} from "~/utils/crops/use-crop-purpose-list";
import { useImportCropScope } from "~/action-panel/components/import-module/components/import-wizard/use-import-crop-scope";

import { AttributeEditModal } from "./attribute-edit-modal";
import { ImportFilter } from "../import-filter";
import { injectIntl, intlShape } from "@ai360/core/intl";
import { config as intlConfig } from "~/intl-provider/config";
import {
    SelectInput,
    NumericInput,
    Loader,
    NoLink,
    Menu,
    TextInput,
    DialogBox,
    DialogBoxFooterType,
} from "~/core";
import * as selectors from "../../../../../selectors";
import * as actions from "../../../../../actions";
import { fetchDropdownData } from "~/core/dropdowns/actions";
import systemAttributes from "~/static/system-attributes.json";
import eventTypes from "~/static/event-types.json";
import { ACTIVE_YN, selectors as picklistSelectors } from "~/core/picklist";
import { adminData } from "~/admin/data";
import { ProductBlendModal } from "~/action-panel/components/common/product-blend-modal";
import * as blendingModels from "~/action-panel/components/common/product-blend-modal/model";
import { messages } from "./i18n-messages";
import { messages as globalMessages } from "~/i18n-messages";
import { IS_SELECTABLE } from "~/utils/products/use-product-list";
import "./crop-filter.css";
import * as blendingSelectors from "~/action-panel/components/common/product-blend-modal/selectors";
import * as blending from "~/action-panel/components/common/product-blend-modal/blending-utils";
import { keywords } from "~/action-panel/components/common/planting/keywords";
import { logFirebaseEvent } from "~/utils/firebase";
import { ISelectOption } from "~/core/components/select-input/model";

const DROPDOWN_CROP = "DROPDOWN_CROP";
const DROPDOWN_BRANDORGANIZATION = "DROPDOWN_BRANDORGANIZATION";
const DROPDOWN_VARIETYHYBRID = "DROPDOWN_VARIETYHYBRID";
const DROPDOWN_YIELDUNIT_DRY = "DROPDOWN_YIELDUNIT_DRY";
const DROPDOWN_YIELDUNIT_LINT = "DROPDOWN_YIELDUNIT_LINT";
const BALE_ACRE = 210;
const BUSHEL_ACRE = 160;
const SACK_ACRE = 212;
const BAG_ACRE = 211;
const BOX_ACRE = 306;
const BARREL_ACRE = 307;
const BIN_ACRE = 308;
const CARTON_ACRE = 309;
const CRATE_ACRE = 310;
const FLAT_ACRE = 311;

export const dropdowns: CropFilterDropdowns = {
    [DROPDOWN_BRANDORGANIZATION]: {
        url: apiUrl("AgBytes/GetBrandOrganizationCropList"),
        model: "",
    },
    [DROPDOWN_VARIETYHYBRID]: {
        url: apiUrl("AgBytes/GetVarietyHybridFilterList"),
        model: { cropId: "", brandOrganization: "" },
    },
    [DROPDOWN_YIELDUNIT_DRY]: {
        url: apiUrl("Picklist/GetUnitPicklistBySystemAttributeAndEventType"),
        model: {
            systemAttribute: systemAttributes.dryYield,
            eventType: eventTypes.harvest,
        },
    },
    [DROPDOWN_YIELDUNIT_LINT]: {
        url: apiUrl("Picklist/GetUnitPicklistBySystemAttributeAndEventType"),
        model: {
            systemAttribute: systemAttributes.cottonLintYield,
            eventType: eventTypes.harvest,
        },
    },
};

interface ICropFilterProps {
    availableProducts?: blendingModels.ProductMixProduct[];
    conversionFactors?: RecAPI.IRecConversionFactor[];
    /** The one customer the import matched, if exactly one; for the blend modal. */
    customerGuid?: string | null;
    DROPDOWN_BRANDORGANIZATION: CropFilterDropdownItem[];
    DROPDOWN_CROP: AgBytesAPI.ICropSelectionListItem[];
    DROPDOWN_VARIETYHYBRID: CropFilterDropdownItem[];
    productBlendPicklists?: blendingModels.ProductBlendPicklists;
    fetchUserCropImportPreferences: (cropGuid: string, yieldIaGuid: string) => void;
    filterItems?: FileImportAPI.CropFilterItem[];
    importFileGuidList?: string[];
    importType?: string;
    intl: intlShape;
    isHarvest: boolean;
    isLoading: boolean;
    isPlanting: boolean;
    refreshDropdowns: (refreshDropdowns: Partial<DropdownRequest> | string) => void;
    removeImportFiles?: (importFileGuidList: string[]) => void;
    assignMatchedToUnmatched?: (
        index: number,
        assignToCrops: (data: any, index: number) => void
    ) => void;
    seedingRateUnits: UnitAPI.IUnitItem[];
    selectItem: (item: FileImportAPI.CropFilterItem) => void;
    selectedFieldGuid?: string;
    setIsLoading: (value: boolean) => void;
    setUserCropImportPreferences: () => void;
    updateAttribute: (item: FileImportAPI.CropFilterItem) => void;
    updateAllFiltersByCrop: (
        cropGuid: string,
        updates: Partial<FileImportAPI.CropFilterItem>
    ) => void;
    updateAlias: (item: FileImportAPI.CropFilterItem) => void;
    updateFilterStatus: (isComplete: boolean) => void;
    updateUserCropImportPreferences: (
        cropGuid: string,
        yieldIaGuid: string,
        averageTestWeight: string,
        averageUnitWeight: string
    ) => void;
    userCropImportPreferences: PreferenceAPI.IUserCropImportPreference;
}

interface ICropFilterState {
    activeProductBlend: number;
    averageTestWeight: string;
    averageUnitWeight: string;
    brandOrganizationGuid: string;
    brandOrganizationName: string;
    cropClassNameGuid: string;
    cropGuid: string;
    cropName: string;
    cropId: number;
    cropPurposeGuid: string;
    cropPurposeName: string;
    addAliasItem: FileImportAPI.CropFilterItem;
    editItem: FileImportAPI.CropFilterItem;
    isEditingAlias: boolean;
    isRefreshingLists: boolean;
    isFetchingPreferences: boolean;
    isSaving: boolean;
    isSeedingRateEnabled: boolean;
    isProductBlendingActive: boolean;
    initialProductMix: blendingModels.ProductMix[];
    lintPercent: number | string;
    options: ImportCropsFilterOption;
    originalProductMixList: blendingModels.ProductMix[];
    seedingRate: string;
    seedingRateIaGuid: string;
    seedingRateUnitModalOpen: boolean;
    seedingRateUnitEdited: boolean;
    userAdjustedSeedingRate: boolean;
    aliasVarietyHybridName: string;
    varietyHybridGuid: string;
    varietyHybridName: string;
    yieldIaGuid: string;
    yieldUnitName: string;
    yieldUnitId: number;
}

interface ImportCropsFilterOption {
    brand: ISelectOption<any>[];
    crop: ISelectOption<any>[];
    // Carries isAgvanceLinked (stamped by getCropPurposeOptionsFromList/
    // stampCropPurposeSelectable) -- isSelectedCropPurposeUnlinked() reads it directly, unlike
    // the plain ISelectOption<any> the sibling `crop` field uses (isSelectedCropUnlinked() checks
    // against the raw DROPDOWN_CROP prop instead, which has no such gap).
    cropPurpose: (ISelectOption<any> & { isAgvanceLinked?: boolean | null })[];
    variety: ISelectOption<any>[];
    yieldUnitDry: ISelectOption<any>[];
    yieldUnitLint: ISelectOption<any>[];
}

interface CropFilterDropdowns {
    DROPDOWN_BRANDORGANIZATION: CropFilterDropdownItem[] | DropdownRequest;
    DROPDOWN_VARIETYHYBRID: CropFilterDropdownItem[] | DropdownRequest;
    DROPDOWN_YIELDUNIT_DRY: CropFilterDropdownItem[] | DropdownRequest;
    DROPDOWN_YIELDUNIT_LINT: CropFilterDropdownItem[] | DropdownRequest;
}

interface CropFilterDropdownItem {
    activeYn: boolean;
    cropGuid?: string;
    fullName?: string;
    guid: string;
    id: string;
    name: string;
    parentGuid: string;
    physicalStateId: string;
}

interface DropdownRequest {
    url: string;
    model: any;
}

class ImportCropFilter_ extends Component<ICropFilterProps, ICropFilterState> {
    static defaultProps = {
        importFileGuidList: [],
        selectedFieldGuid: null,
    };

    constructor(props) {
        super(props);

        this.state = {
            isRefreshingLists: false,
            isFetchingPreferences: false,
            addAliasItem: null,
            editItem: null,
            cropGuid: null,
            cropName: null,
            cropId: -1,
            cropPurposeGuid: null,
            cropPurposeName: null,
            brandOrganizationGuid: null,
            brandOrganizationName: null,
            aliasVarietyHybridName: null,
            varietyHybridGuid: null,
            varietyHybridName: null,
            yieldIaGuid: null,
            yieldUnitName: null,
            yieldUnitId: -1,
            averageTestWeight: "",
            averageUnitWeight: "",
            lintPercent: "",
            options: {
                crop: ImportCropFilter_.getCropOptionsFromList(
                    props[DROPDOWN_CROP],
                    props.intl.formatMessage
                ),
                cropPurpose: [],
                brand: ImportCropFilter_.getOptionsFromList(
                    props[DROPDOWN_BRANDORGANIZATION],
                    "guid",
                    "name"
                ),
                variety: ImportCropFilter_.getOptionsFromList(
                    props[DROPDOWN_VARIETYHYBRID],
                    "guid",
                    "name",
                    "parentGuid"
                ),
                yieldUnitDry: ImportCropFilter_.getOptionsFromList(
                    props[DROPDOWN_YIELDUNIT_DRY],
                    "importAttributeGuid",
                    "abbreviation"
                ),
                yieldUnitLint: ImportCropFilter_.getOptionsFromList(
                    props[DROPDOWN_YIELDUNIT_LINT],
                    "importAttributeGuid",
                    "abbreviation"
                ),
            },
            isEditingAlias: false,
            isSaving: false,
            isSeedingRateEnabled: false,
            isProductBlendingActive: false,
            activeProductBlend: -1,
            originalProductMixList: [],
            initialProductMix: [],
            cropClassNameGuid: null,
            seedingRate: null,
            seedingRateIaGuid: null,
            seedingRateUnitModalOpen: false,
            seedingRateUnitEdited: false,
            userAdjustedSeedingRate: false,
        };
    }

    onUpdateProductBlending(productMix: blendingModels.ProductMix): void {
        const productMixList = [...this.state.originalProductMixList];
        productMixList.splice(this.state.activeProductBlend, 1, productMix);
        this.setState({ originalProductMixList: productMixList });
    }

    onSaveProductBlending(): void {
        const productMixList = [...this.state.originalProductMixList];
        this.setState({ initialProductMix: productMixList }, () => {
            this.onCloseProductBlending();
        });
    }

    onCancelProductBlending(): void {
        this.onCloseProductBlending();
    }

    onCloseProductBlending(): void {
        const initialMix = this.state.initialProductMix ? [...this.state.initialProductMix] : [];
        this.setState({
            isProductBlendingActive: false,
            activeProductBlend: -1,
            originalProductMixList: [...initialMix],
        });
    }

    componentDidUpdate(prevProps: ICropFilterProps) {
        const newOptions: Partial<ImportCropsFilterOption> = {};
        const replacementState: Partial<ICropFilterState> = {};
        let optionsChanged = false;

        if (this.props[DROPDOWN_CROP] !== prevProps[DROPDOWN_CROP]) {
            newOptions.crop = ImportCropFilter_.getCropOptionsFromList(
                this.props[DROPDOWN_CROP],
                this.props.intl.formatMessage
            );
            optionsChanged = true;
            this.setState({ isRefreshingLists: false });
        }
        if (this.props[DROPDOWN_BRANDORGANIZATION] !== prevProps[DROPDOWN_BRANDORGANIZATION]) {
            newOptions.brand = ImportCropFilter_.getOptionsFromList(
                this.props[DROPDOWN_BRANDORGANIZATION],
                "guid",
                "name"
            );
            optionsChanged = true;
            this.setState({ isRefreshingLists: false });
        }
        if (this.props[DROPDOWN_VARIETYHYBRID] !== prevProps[DROPDOWN_VARIETYHYBRID]) {
            newOptions.variety = ImportCropFilter_.getOptionsFromList(
                this.props[DROPDOWN_VARIETYHYBRID],
                "guid",
                "name",
                "parentGuid"
            );
            optionsChanged = true;
            this.setState({ isRefreshingLists: false });
        }
        if (this.props[DROPDOWN_YIELDUNIT_DRY] !== prevProps[DROPDOWN_YIELDUNIT_DRY]) {
            newOptions.yieldUnitDry = ImportCropFilter_.getOptionsFromList(
                this.props[DROPDOWN_YIELDUNIT_DRY],
                "importAttributeGuid",
                "abbreviation"
            );
            optionsChanged = true;
            this.setState({ isRefreshingLists: false });
        }
        if (this.props[DROPDOWN_YIELDUNIT_LINT] !== prevProps[DROPDOWN_YIELDUNIT_LINT]) {
            newOptions.yieldUnitLint = ImportCropFilter_.getOptionsFromList(
                this.props[DROPDOWN_YIELDUNIT_LINT],
                "importAttributeGuid",
                "abbreviation"
            );
            optionsChanged = true;
            this.setState({ isRefreshingLists: false });
        }

        if (this.props.userCropImportPreferences !== prevProps.userCropImportPreferences) {
            const { yieldIaGuid, averageTestWeight, averageUnitWeight } =
                this.props.userCropImportPreferences;
            const yieldUnitId = this.getYieldUnitId(this.state.cropId, yieldIaGuid);

            this.setState({
                isRefreshingLists: false,
                isFetchingPreferences: false,
                yieldUnitId,
                yieldIaGuid,
            });
            if (ImportCropFilter_.isBushelPerAcre(yieldUnitId)) {
                // if we are using bushels, set the average test weight pref
                this.setState({ averageTestWeight });
            } else if (ImportCropFilter_.isVolumetricFlowRate(replacementState.yieldUnitId)) {
                // else we set average unit weight pref for other unit types
                this.setState({ averageUnitWeight });
            }
        }

        if (optionsChanged) {
            this.setState({
                options: {
                    ...this.state.options,
                    ...newOptions,
                },
            });
        }

        if (
            this.props.isLoading &&
            !(this.state.isRefreshingLists || this.state.isFetchingPreferences)
        ) {
            this.props.setIsLoading(false);
        }
    }

    componentDidMount() {
        this.refreshCropDropdownLists(true);
    }

    getLabel = ({ options = [], guid = "" }, valueKey = "value", labelKey = "label") => {
        if (options.length) {
            const selectedOption = options.find((option) => guid === option[valueKey]);
            return selectedOption ? selectedOption[labelKey] : "";
        }
        return "";
    };

    static getOptionsFromList(listData, keyProp: string, nameProp: string, parentKeyProp?: string) {
        if (!listData) {
            return [];
        }
        return listData.map((item) => {
            return {
                value: item[keyProp],
                label: item[nameProp],
                activeYn:
                    item[adminData.PROPS_ACTIVE_YN] != null
                        ? item[adminData.PROPS_ACTIVE_YN]
                        : true,
                parentValue: parentKeyProp ? item[parentKeyProp] : null,
            };
        });
    }

    // Like getOptionsFromList, but also stamps isSelectable/getToolTip from isAgvanceLinked,
    // which only the raw crop list (AgBytesAPI.ICropSelectionListItem[]) carries. listData is
    // always useCropList()'s result (see ImportCropFilter below), so activeYn is always
    // present — unlike the sibling getOptionsFromList, no null-default needed here.
    static getCropOptionsFromList(listData, formatMessage) {
        if (!listData) {
            return [];
        }
        return stampCropSelectable(
            listData.map((item) => ({
                value: item.cropGuid,
                label: item.name,
                activeYn: item.activeYn,
                isAgvanceLinked: item.isAgvanceLinked,
            })),
            formatMessage
        );
    }

    // Mirrors getCropOptionsFromList -- listData is CropPurposeDataHost's useCropPurposeList()
    // result (raw AgBytesAPI.ICropPurposeSelectionListItem[]), scoped to the modal's currently
    // selected cropGuid.
    static getCropPurposeOptionsFromList(listData, formatMessage) {
        if (!listData) {
            return [];
        }
        return stampCropPurposeSelectable(
            listData.map((item) => ({
                value: item.cropPurposeGuid,
                label: item.name,
                activeYn: item.activeYn,
                isAgvanceLinked: item.isAgvanceLinked,
            })),
            formatMessage
        );
    }

    getSeedingRateUnitAbb(): string {
        const { seedingRateIaGuid } = this.state;
        const targetOption = this.props.seedingRateUnits?.find(
            (x) => x.importAttributeGuid === seedingRateIaGuid || x.unitGuid === seedingRateIaGuid
        );
        return targetOption?.abbreviation;
    }

    getSeedingRateIaGuidFromUnit(unitGuid: string): string {
        const targetOption = this.props.seedingRateUnits.find((x) => x.unitGuid === unitGuid);
        return targetOption?.importAttributeGuid;
    }

    getUnitGuidFromIa() {
        const targetOption = this.props.seedingRateUnits.find(
            (x) => x.importAttributeGuid === this.state.seedingRateIaGuid
        );
        return targetOption?.unitGuid;
    }

    getDefaultSeedingIaGuid() {
        return this.props.seedingRateUnits?.find((x) => x.abbreviation?.toLowerCase() === "seed/ac")
            ?.importAttributeGuid;
    }

    formatSeedingRate(value: string): string {
        const { formatNumber } = this.props.intl;
        if (!this.state.userAdjustedSeedingRate) {
            const isKsdsPerAcre = this.getSeedingRateUnitAbb() === keywords.ksdsPerAc;
            const decimals = FormattingHelpers.progressiveRoundingDecimalsThreeTier(value);
            return isKsdsPerAcre
                ? value
                : formatNumber(value, intlConfig.customFormatOptions(decimals));
        }
        return value;
    }

    adjustSeedingRateUnitsByCrop() {
        const updatedProps: Partial<FileImportAPI.CropFilterItem> = {
            seedingRateUnitAbbreviation: this.getSeedingRateUnitAbb(),
            seedingRateUnitGuid: this.getUnitGuidFromIa(),
        };

        this.props.updateAllFiltersByCrop(this.state.editItem.cropGuid, updatedProps);
    }

    static isVolumetricFlowRate(yieldUnitId: number): boolean {
        const volumetricFlowRateIds = [
            BALE_ACRE,
            SACK_ACRE,
            BAG_ACRE,
            BOX_ACRE,
            BARREL_ACRE,
            BIN_ACRE,
            CARTON_ACRE,
            CRATE_ACRE,
            FLAT_ACRE,
        ];
        const unitIndex = volumetricFlowRateIds.indexOf(yieldUnitId);

        return unitIndex !== -1;
    }
    static isBushelPerAcre(yieldUnitId: number): boolean {
        return yieldUnitId === BUSHEL_ACRE;
    }
    static isCottonCrop(cropId: number): boolean {
        return cropId === 7;
    }
    isAnyMatchingCrop(): boolean {
        return this.props.filterItems.some(
            (f) => f.cropGuid === this.state.editItem.cropGuid && !f.isSelected
        );
    }

    filterProperties = !this.props.isPlanting
        ? [
              {
                  isRequired: true,
                  label: this.props.intl.formatMessage(messages.cropText),
                  propertyName: "cropName",
                  propertyGuid: "cropGuid",
                  cellSize: this.props.isHarvest ? 25 : 20,
              },
              {
                  isRequired: false,
                  label: this.props.intl.formatMessage(messages.brandVarietyText),
                  propertyGuid: ["brandOrganizationGuid", "varietyHybridGuid"],
                  propertyName: ["brandOrganizationName", "varietyHybridName"],
                  cellSize: this.props.isHarvest ? 30 : 25,
              },
              {
                  isRequired: true,
                  label: this.props.intl.formatMessage(messages.yieldUnitText),
                  propertyName: "yieldUnitName",
                  propertyGuid: "yieldIaGuid",
                  cellSize: 15,
              },
              {
                  isRequired: false,
                  label: this.props.intl.formatMessage(messages.featuresText),
                  propertyName: "pointCount",
                  format: "numeric",
                  cellSize: 20,
              },
          ]
        : [
              {
                  isRequired: true,
                  label: this.props.intl.formatMessage(messages.cropText),
                  propertyName: "cropName",
                  propertyGuid: "cropGuid",
                  cellSize: 15,
              },
              {
                  isRequired: true,
                  label: this.props.intl.formatMessage(messages.brandVarietyText),
                  propertyGuid: ["brandOrganizationGuid", "varietyHybridGuid"],
                  propertyName: ["brandOrganizationName", "varietyHybridName"],
                  cellSize: 40,
              },
              {
                  isRequired: false,
                  label: this.props.intl.formatMessage(messages.seedingRateText),
                  propertyName: "seedingRate",
                  format: "numeric",
                  cellSize: 20,
              },
              {
                  isRequired: false,
                  label: this.props.intl.formatMessage(messages.featuresText),
                  propertyName: "pointCount",
                  format: "numeric",
                  cellSize: 15,
              },
          ];

    closeSeedingRateUnitModal = (): void => {
        this.setState({ seedingRateUnitModalOpen: false, seedingRateUnitEdited: false });
        this.onSave();
    };

    applySeedingRateUnitToMultiple = (): void => {
        this.setState({ seedingRateUnitModalOpen: false });
        this.adjustSeedingRateUnitsByCrop();
    };

    onEditAttribute = (item): void => {
        const cropId = this.getCropId(item.cropGuid);
        const yieldUnitId = this.getYieldUnitId(cropId, item.yieldIaGuid);

        this.setState(
            {
                editItem: item,
                cropGuid: item.cropGuid,
                cropId: cropId,
                cropPurposeGuid: item.cropPurposeGuid,
                brandOrganizationGuid: item.brandOrganizationGuid,
                aliasVarietyHybridName: item.aliasVarietyHybridName,
                varietyHybridGuid: item.varietyHybridGuid,
                yieldIaGuid: item.yieldIaGuid,
                yieldUnitId: yieldUnitId,
                averageTestWeight: item.averageTestWeight,
                averageUnitWeight: item.averageUnitWeight,
                lintPercent: item.lintPercent,
                originalProductMixList: item.productMixList,
                initialProductMix: item.productMixList ? item.productMixList : [],
                isSeedingRateEnabled: this.seedingRateEditable(item),
                seedingRate: item.seedingRate,
                seedingRateIaGuid: item.seedingRateUnitGuid
                    ? this.getSeedingRateIaGuidFromUnit(item.seedingRateUnitGuid)
                    : this.getDefaultSeedingIaGuid(),
            },
            () => {
                this.refreshCropDropdownLists();
            }
        );
    };

    getUpdatedItemForSave = (): FileImportAPI.CropFilterItem => {
        const cropId = this.getCropId(this.state.cropGuid);

        const cropName = this.getLabel({
            options: this.state.options.crop,
            guid: this.state.cropGuid,
        });

        const cropPurposeName = this.getLabel({
            options: this.state.options.cropPurpose,
            guid: this.state.cropPurposeGuid,
        });

        const brandAndVariety = this.state.brandOrganizationGuid && this.state.varietyHybridGuid;
        const brandOrganizationName = brandAndVariety
            ? this.getLabel({
                  options: this.state.options.brand,
                  guid: this.state.brandOrganizationGuid,
              })
            : "";
        const varietyHybridName = brandAndVariety
            ? this.getLabel({
                  options: this.state.options.variety,
                  guid: this.state.varietyHybridGuid,
              })
            : "";

        const yieldOptions = ImportCropFilter_.isCottonCrop(cropId)
            ? this.state.options.yieldUnitLint
            : this.state.options.yieldUnitDry;
        const yieldUnitName = this.getLabel({
            options: yieldOptions,
            guid: this.state.yieldIaGuid,
        });

        const { cropGuid, yieldIaGuid, averageTestWeight, averageUnitWeight, initialProductMix } =
            this.state;

        const item: FileImportAPI.CropFilterItem = {
            ...this.state.editItem,
            cropGuid: cropGuid,
            cropName: cropName,
            cropPurposeGuid: this.state.cropPurposeGuid,
            cropPurposeName: cropPurposeName,
            brandOrganizationGuid: this.state.brandOrganizationGuid,
            brandOrganizationName: brandOrganizationName,
            aliasVarietyHybridName: this.state.aliasVarietyHybridName,
            varietyHybridGuid: this.state.varietyHybridGuid,
            varietyHybridName: varietyHybridName,
            yieldIaGuid: yieldIaGuid,
            yieldUnitName: yieldUnitName,
            averageTestWeight: averageTestWeight,
            averageUnitWeight: averageUnitWeight,
            lintPercent: this.state.lintPercent,
            hasLookupMatch: true,
            productMixList: initialProductMix,
            seedingRate: this.state.seedingRate,
            seedingRateUnitAbbreviation: this.state.seedingRateIaGuid
                ? this.getSeedingRateUnitAbb()
                : null,
            seedingRateUnitGuid: this.getUnitGuidFromIa(),
        };
        return item;
    };

    onSaveEdit = (): void => {
        const { seedingRateUnitEdited, seedingRateUnitModalOpen } = this.state;
        if (seedingRateUnitEdited && !seedingRateUnitModalOpen && this.isAnyMatchingCrop()) {
            this.setState({ seedingRateUnitModalOpen: true });
            return;
        }
        this.onSave();
    };

    onSave = (): void => {
        const { cropGuid, yieldIaGuid, averageTestWeight, averageUnitWeight } = this.state;

        if (!this.props.isPlanting) {
            this.props.updateUserCropImportPreferences(
                cropGuid,
                yieldIaGuid,
                averageTestWeight,
                averageUnitWeight
            );
        }

        this.props.updateAttribute(this.getUpdatedItemForSave());
        this.onCloseEdit();
    };

    onCloseEdit = (): void => {
        this.setState({
            editItem: null,
            cropGuid: null,
            cropPurposeGuid: null,
            cropPurposeName: null,
            brandOrganizationGuid: null,
            aliasVarietyHybridName: "",
            varietyHybridGuid: null,
            yieldIaGuid: null,
            yieldUnitId: -1,
            averageTestWeight: "",
            averageUnitWeight: "",
            lintPercent: "",
            isSaving: false,
            seedingRate: null,
            seedingRateIaGuid: null,
            userAdjustedSeedingRate: false,
        });
    };

    onAddAlias = (item, index): void => {
        item.index = index;
        const cropId = this.getCropId(item.cropGuid);
        const yieldUnitId = this.getYieldUnitId(cropId, item.yieldIaGuid);

        this.setState(
            {
                addAliasItem: item,
                cropGuid: item.cropGuid,
                cropName: item.cropName,
                cropId: cropId,
                cropPurposeGuid: item.cropPurposeGuid,
                brandOrganizationGuid: item.brandOrganizationGuid,
                brandOrganizationName: item.brandOrganizationName,
                aliasVarietyHybridName: item.aliasVarietyHybridName,
                varietyHybridGuid: item.varietyHybridGuid,
                varietyHybridName: item.varietyHybridName,
                yieldIaGuid: item.yieldIaGuid,
                yieldUnitId: yieldUnitId,
                yieldUnitName: item.yieldUnitName,
                averageTestWeight: item.averageTestWeight,
                averageUnitWeight: item.averageUnitWeight,
                lintPercent: item.lintPercent,
                originalProductMixList: item.productMixList,
                initialProductMix: item.productMixList ? item.productMixList : [],
                isSeedingRateEnabled: this.seedingRateEditable(item),
                seedingRate: item.seedingRate,
                seedingRateIaGuid: this.getSeedingRateIaGuidFromUnit(item.seedingRateUnitGuid),
            },
            () => {
                this.refreshCropDropdownLists();
            }
        );
    };

    onSaveAlias = (): void => {
        const item: FileImportAPI.CropFilterItem = {
            ...this.state.addAliasItem,
            isAliasEdit: true,
            cropGuid: this.state.cropGuid,
            cropName: this.state.cropName,
            cropPurposeGuid: this.state.cropPurposeGuid,
            cropPurposeName: this.state.cropPurposeName,
            brandOrganizationGuid: this.state.brandOrganizationGuid,
            brandOrganizationName: this.state.brandOrganizationName,
            aliasVarietyHybridName: this.state.aliasVarietyHybridName,
            varietyHybridGuid: this.state.varietyHybridGuid,
            varietyHybridName: this.state.varietyHybridName,
            yieldIaGuid: this.state.yieldIaGuid,
            yieldUnitName: this.state.yieldUnitName,
            averageTestWeight: this.state.averageTestWeight,
            averageUnitWeight: this.state.averageUnitWeight,
            lintPercent: this.state.lintPercent,
            hasLookupMatch: true,
            productMixList: this.state.initialProductMix,
            seedingRate: this.state.seedingRate,
            seedingRateUnitAbbreviation: this.state.seedingRateIaGuid
                ? this.getSeedingRateUnitAbb()
                : null,
            seedingRateUnitGuid: this.getUnitGuidFromIa(),
        };

        this.props.updateAlias(item);
        this.onCloseAlias();
    };

    onCloseAlias = (): void => {
        const { aliasVarietyHybridName } = this.state;
        this.setState({
            isEditingAlias: Boolean(aliasVarietyHybridName),
            addAliasItem: null,
            cropGuid: null,
            cropPurposeGuid: null,
            cropPurposeName: null,
            brandOrganizationGuid: null,
            aliasVarietyHybridName: "",
            varietyHybridGuid: null,
            yieldIaGuid: null,
            yieldUnitId: -1,
            averageTestWeight: "",
            averageUnitWeight: "",
            lintPercent: "",
            isSaving: false,
            seedingRate: null,
            seedingRateIaGuid: null,
            userAdjustedSeedingRate: false,
        });
    };

    refreshCropDropdownLists = (isInitialLoad = false): void => {
        this.props.setIsLoading(true);
        this.setState({
            isRefreshingLists: true,
        });
        const { cropGuid, brandOrganizationGuid } = this.state;
        const refreshDropdowns: Partial<DropdownRequest> = {};
        if (isInitialLoad) {
            if (!this.props.isPlanting) {
                refreshDropdowns[DROPDOWN_YIELDUNIT_DRY] = dropdowns[DROPDOWN_YIELDUNIT_DRY];
                refreshDropdowns[DROPDOWN_YIELDUNIT_LINT] = dropdowns[DROPDOWN_YIELDUNIT_LINT];
            }
        }
        refreshDropdowns[DROPDOWN_BRANDORGANIZATION] = {
            url: apiUrl("AgBytes/GetBrandOrganizationCropList"),
            model: cropGuid,
        };
        refreshDropdowns[DROPDOWN_VARIETYHYBRID] = {
            url: apiUrl("AgBytes/GetVarietyHybridFilterList"),
            model: {
                cropId: cropGuid,
                brandOrganization: brandOrganizationGuid,
            },
        };

        this.props.refreshDropdowns(refreshDropdowns);
    };

    getYieldUnitId(cropId: number, yieldIaGuid: string | number): number {
        if (this.props.isPlanting) {
            return null;
        }
        let yieldUnit;
        if (this.props[DROPDOWN_YIELDUNIT_DRY] && this.props[DROPDOWN_YIELDUNIT_LINT]) {
            yieldUnit = !ImportCropFilter_.isCottonCrop(cropId)
                ? this.props[DROPDOWN_YIELDUNIT_DRY].find((unit) => {
                      return unit.importAttributeGuid === yieldIaGuid;
                  })
                : this.props[DROPDOWN_YIELDUNIT_LINT].find((unit) => {
                      return unit.importAttributeGuid === yieldIaGuid;
                  });
        }
        return yieldUnit ? +yieldUnit.unitId : -1;
    }

    getCropId(cropGuid: string): number {
        let selectedCrop;
        if (this.props[DROPDOWN_CROP]) {
            selectedCrop = this.props[DROPDOWN_CROP].find((crop) => {
                return crop.cropGuid === cropGuid;
            });
        }
        return selectedCrop ? +selectedCrop.cropId : -1;
    }

    // Checked against the raw DROPDOWN_CROP list (cropGuid-keyed), not the ICropDropdownOption
    // shape (guid-keyed) that the shared isCropUnlinked utility expects.
    isSelectedCropUnlinked(): boolean {
        const { cropGuid } = this.state;
        if (!cropGuid || !this.props[DROPDOWN_CROP]) {
            return false;
        }
        const selectedCrop = this.props[DROPDOWN_CROP].find((crop) => crop.cropGuid === cropGuid);
        return selectedCrop != null && selectedCrop.isAgvanceLinked === false;
    }

    // A crop picked under an earlier customer scope may be unlinked under the current one.
    isItemCropLinked = (item: FileImportAPI.CropFilterItem): boolean => {
        const crop = item.cropGuid
            ? this.props[DROPDOWN_CROP]?.find((c) => c.cropGuid === item.cropGuid)
            : null;
        return crop?.isAgvanceLinked !== false;
    };

    // Checked against `state.options.cropPurpose` (value-keyed, stamped by CropPurposeDataHost),
    // not the ICropPurposeDropdownOption shape (guid-keyed) the shared isCropPurposeUnlinked
    // utility expects -- mirrors isSelectedCropUnlinked's own reasoning above.
    isSelectedCropPurposeUnlinked(): boolean {
        const { cropPurposeGuid } = this.state;
        if (!cropPurposeGuid) {
            return false;
        }
        const selectedCropPurpose = this.state.options.cropPurpose.find(
            (option) => option.value === cropPurposeGuid
        );
        return selectedCropPurpose != null && selectedCropPurpose.isAgvanceLinked === false;
    }

    onCropPurposeOptionsChange = (options: ImportCropsFilterOption["cropPurpose"]): void => {
        this.setState({
            options: {
                ...this.state.options,
                cropPurpose: options,
            },
        });
    };

    updateCropModelSeedingRate = (newValue: string): void => {
        this.setState({
            seedingRate: newValue,
        });
    };

    updateCropModelSeedingRateUnit = (newValue: string): void => {
        this.setState({
            seedingRateIaGuid: newValue,
        });
    };

    updateCropModel = (propertyName: string, newValue: string | number): void => {
        const { cropGuid, cropPurposeGuid, brandOrganizationGuid, varietyHybridGuid } = this.state;
        const newEditProps: Partial<ICropFilterState> = {
            cropGuid,
            cropPurposeGuid,
            brandOrganizationGuid,
            varietyHybridGuid,
        };
        let refreshCropDropdowns = false;
        /* eslint-disable no-fallthrough */
        switch (propertyName) {
            case "cropGuid": {
                newEditProps.cropId = this.getCropId(newValue as string);
                newEditProps.cropClassNameGuid = "";
                newEditProps.yieldIaGuid = "";
                newEditProps.lintPercent = ImportCropFilter_.isCottonCrop(newEditProps.cropId)
                    ? 35
                    : "";
                newEditProps.isFetchingPreferences = true;
                newEditProps.averageTestWeight = "";
                newEditProps.averageUnitWeight = "";
                newEditProps.brandOrganizationGuid = "";
                newEditProps.varietyHybridGuid = "";
                refreshCropDropdowns = true;
            }
            case "cropPurposeGuid": {
                newEditProps.cropPurposeGuid = cropPurposeGuid;
                break;
            }
            case "brandOrganizationGuid":
                newEditProps.varietyHybridGuid = "";
                refreshCropDropdowns = true;
                break;
            case "varietyHybridGuid": {
                const varietyOption = this.state.options.variety.find((v) => v.value === newValue);
                if (varietyOption) {
                    newEditProps.brandOrganizationGuid = varietyOption.parentValue;
                    refreshCropDropdowns =
                        this.state.brandOrganizationGuid !== newEditProps.brandOrganizationGuid;
                }
                break;
            }
            case "yieldIaGuid": {
                newEditProps.yieldUnitId = this.getYieldUnitId(this.state.cropId, newValue);
                newEditProps.isFetchingPreferences = true;
                newEditProps.averageTestWeight = "";
                newEditProps.averageUnitWeight = "";
                break;
            }
            default:
                break;
        }
        /* eslint-enable no-fallthrough */
        newEditProps[propertyName] = newValue;

        this.setState(
            {
                ...this.state,
                ...newEditProps,
            },
            () => {
                if (refreshCropDropdowns) {
                    this.refreshCropDropdownLists();
                }
                if (propertyName === "cropGuid" || propertyName === "yieldIaGuid") {
                    this.props.setIsLoading(true);
                    this.props.fetchUserCropImportPreferences(
                        this.state.cropGuid,
                        this.state.yieldIaGuid
                    );
                }
            }
        );
    };

    canSaveEdit = (): boolean => {
        const {
            averageTestWeight,
            averageUnitWeight,
            brandOrganizationGuid,
            cropGuid,
            cropId,
            lintPercent,
            varietyHybridGuid,
            yieldIaGuid,
            yieldUnitId,
        } = this.state;
        return Boolean(
            cropGuid &&
                !this.isSelectedCropUnlinked() &&
                !this.isSelectedCropPurposeUnlinked() &&
                (yieldIaGuid || this.props.isPlanting) &&
                (!this.props.isPlanting ||
                    (brandOrganizationGuid && varietyHybridGuid && this.seedingRateSavable())) &&
                ((ImportCropFilter_.isBushelPerAcre(yieldUnitId) && averageTestWeight) ||
                    (ImportCropFilter_.isVolumetricFlowRate(yieldUnitId) && averageUnitWeight) ||
                    (!ImportCropFilter_.isBushelPerAcre(yieldUnitId) &&
                        !ImportCropFilter_.isVolumetricFlowRate(yieldUnitId))) &&
                (!ImportCropFilter_.isCottonCrop(cropId) || lintPercent !== "")
        );
    };

    canSaveAlias = (): boolean => {
        const { aliasVarietyHybridName } = this.state;
        return Boolean(aliasVarietyHybridName) || this.state.isEditingAlias;
    };

    assignToCrops(data, index: number) {
        const from = data.crops[index];

        const copy = (to) => ({
            ...from,
            importFiles: to.importFiles,
            hasLookupMatch: true,
            isSelected: to.isSelected,
            rawBrand: to.rawBrand,
            rawCrop: to.rawCrop,
            rawVariety: to.rawVariety,
        });

        return {
            ...data,
            crops: data.crops.map((to) => (to.hasLookupMatch ? to : copy(to))),
        };
    }

    onAddEditProductMix(index = -1): void {
        const isAdd = index === -1;
        let mixIndex = index;
        let originalProductMixList = this.state.originalProductMixList
            ? [...this.state.originalProductMixList]
            : [];
        if (isAdd) {
            const productMixList = [...originalProductMixList, new blendingModels.ProductMix("")];
            mixIndex = productMixList.length - 1;
            originalProductMixList = productMixList;
        }
        this.setState({
            isProductBlendingActive: true,
            activeProductBlend: mixIndex,
            originalProductMixList: originalProductMixList,
        });
    }

    _updateTargetRate(index: number, newTargetRate: string): void {
        const { availableProducts, conversionFactors, productBlendPicklists } = this.props;
        const productMixList = this.state.originalProductMixList;
        const newProductMixList = productMixList.map((mix, mixIndex) => {
            if (
                mixIndex === index &&
                blending.roundValue(newTargetRate, 2) !== blending.roundValue(mix.targetRate, 2)
            ) {
                const blendingProps = {
                    availableProducts,
                    conversionFactors,
                    productBlendPicklists,
                };
                const lineRatio = Number(newTargetRate) / Number(mix.targetRate);
                const newCostPerAcre = mix.costPerAcre * lineRatio;
                const newTotalProduct = mix.totalProduct * lineRatio;
                const newTotalCost = mix.totalCost * lineRatio;
                return {
                    ...mix,
                    actualRate: newTargetRate,
                    targetRate: newTargetRate,
                    costPerAcre: newCostPerAcre,
                    totalProduct: newTotalProduct,
                    totalCost: newTotalCost,
                    products: mix.products.map((product) => {
                        const rate =
                            newTargetRate && product.rate === 0
                                ? blending.getProductRateInMix(
                                      product.density.toString(),
                                      {
                                          ...mix,
                                          targetRate: newTargetRate,
                                      },
                                      product,
                                      blending.getRateUnit(
                                          product.rateUnitGuid,
                                          blendingProps.productBlendPicklists
                                      ),
                                      blendingProps
                                  )
                                : product.rate * lineRatio;
                        const lineRateToLineCostConversionFactor =
                            blending.getProductRateConversionFactor(
                                product.rateUnitGuid,
                                product.costUnitGuid,
                                product.density,
                                newTargetRate,
                                blendingProps
                            );
                        const calculatedArea = 1;
                        const totalProduct =
                            newTargetRate && product.rate === 0
                                ? rate * lineRateToLineCostConversionFactor * calculatedArea
                                : product.totalProduct * lineRatio;
                        const totalCost = totalProduct * product.cost;
                        const costPerAcre = totalCost / calculatedArea;
                        return {
                            ...product,
                            rate,
                            costPerAcre,
                            totalProduct,
                            totalCost,
                        };
                    }),
                };
            }
            return mix;
        });
        this.setState({ originalProductMixList: newProductMixList });
    }

    onRemoveProductMix(index: number): void {
        const productMixList = [...this.state.initialProductMix];
        productMixList.splice(index, 1);
        this.setState({
            initialProductMix: productMixList,
            originalProductMixList: productMixList,
        });
    }

    _getMenuItems(index: number) {
        const { formatMessage } = this.props.intl;
        return [
            {
                label: formatMessage(messages.editProductMixText),
                action: () => this.onAddEditProductMix(index),
            },
            {
                label: formatMessage(messages.removeProductMixText),
                action: () => this.onRemoveProductMix(index),
            },
        ].map((menuItem, key) => {
            return { ...menuItem, key };
        });
    }

    seedingRateSavable(): boolean {
        const { seedingRate, seedingRateIaGuid } = this.state;
        return Boolean(
            (!seedingRate && !seedingRateIaGuid) ||
                seedingRate === "0" ||
                (seedingRate && seedingRateIaGuid)
        );
    }

    seedingRateEditable(item: FileImportAPI.CropFilterItem): boolean {
        const editSeeding =
            !item.seedingRateOriginal ||
            Number(item.seedingRateOriginal) === 0 ||
            item.seedingRateOriginal === "0"
                ? true
                : false;
        return editSeeding;
    }

    //work around since the menu currently supports the in build window object
    triggerClick = (): void => {
        const evObj = document.createEvent("MouseEvents");
        evObj.initMouseEvent(
            "click",
            true,
            true,
            window,
            1,
            1,
            1,
            1,
            1,
            false,
            false,
            false,
            false,
            0,
            null
        );
        document.dispatchEvent(evObj);
    };

    buildProducts = (): JSX.Element => {
        const { formatMessage } = this.props.intl;
        const addMixStyle: CSSProperties = {};
        const initialProductMix = this.state.initialProductMix;
        if (initialProductMix && initialProductMix.length === 0) {
            addMixStyle.paddingTop = 0;
            addMixStyle.position = "relative";
            addMixStyle.top = 20;
            addMixStyle.paddingLeft = 5;
        } else {
            addMixStyle.top = 0;
            addMixStyle.paddingLeft = 5;
        }
        return (
            <div onClick={() => this.triggerClick()}>
                <div className="product-mixes-table">
                    {initialProductMix &&
                        initialProductMix.map((mix, index) => {
                            const mixNumber = index + 1;
                            const mixName =
                                mix.products.length <= 1 &&
                                !(mix.products.length === 1 && mix.products[0].customProductGuid)
                                    ? ""
                                    : mix.name;
                            const mixTitle = `${formatMessage(messages.mixText)} ${mixNumber}`;
                            const isMixLocked =
                                mix.isLockGuaranteedAnalysis ||
                                mix.isLockProductRatios ||
                                !this.state.isProductBlendingActive;
                            return (
                                <div key={`mix-${index}`} className="product-mixes-rows">
                                    <div className="product-name-column">
                                        <div className="mix-title" title={mixTitle}>
                                            {mixTitle}:
                                        </div>
                                        <div className="mix-product-list">
                                            {mixName.length === 0 ? (
                                                mix.products
                                                    .filter((product) => !product.customProductGuid)
                                                    .map((product, pIndex) => (
                                                        <div
                                                            key={`product-${index}-${pIndex}`}
                                                            className="product-row"
                                                        >
                                                            {product.productName}
                                                        </div>
                                                    ))
                                            ) : (
                                                <div className="product-row"> {mixName}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="rate-input">
                                        {isMixLocked ? (
                                            <NumericInput
                                                scale={2}
                                                precision={11}
                                                onChange={(strValue, fmtValue, numValue) =>
                                                    this._updateTargetRate(index, numValue)
                                                }
                                                value={mix.targetRate}
                                                showTopLabel={false}
                                                testId={`crop-filter-target-rate-input-${index}`}
                                            />
                                        ) : (
                                            <div className="rate-unit">{mix.targetRate}</div>
                                        )}
                                    </div>
                                    <div className="rate-unit">{mix.targetRateUnit}</div>
                                    <div className="product-menu">
                                        <Menu
                                            className="context-menu"
                                            isDotMenu={true}
                                            getMenuItems={() => this._getMenuItems(index)}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                </div>
                <div className="add-mix" style={addMixStyle}>
                    <NoLink
                        label={formatMessage(messages.addProductMixText)}
                        onClick={() => {
                            logFirebaseEvent("import_planting_add_product_mix");
                            this.onAddEditProductMix();
                        }}
                    />
                </div>
            </div>
        );
    };

    buildSeedingRateUnitModal = (): JSX.Element => {
        const { formatMessage } = this.props.intl;
        return (
            <DialogBox
                isOpen={this.state.seedingRateUnitModalOpen}
                isModal={true}
                title={formatMessage(messages.seedingRateUnitUpdateTitle)}
                draggable={true}
                hideCloseX={true}
                onAction={this.applySeedingRateUnitToMultiple}
                onClose={this.closeSeedingRateUnitModal}
                footerType={DialogBoxFooterType.YES_NO}
                testId="crop-filter-seeding-rate-unit-modal"
            >
                {formatMessage(messages.seedingRateUnitUpdateText)}
            </DialogBox>
        );
    };

    render() {
        const { formatMessage } = this.props.intl;
        const formattedSeedingRate = this.formatSeedingRate(this.state.seedingRate);
        const isSelectedCropUnlinked = this.isSelectedCropUnlinked();
        const cropNotLinkedText = isSelectedCropUnlinked
            ? formatMessage(globalMessages.cropNotLinkedInAgvance)
            : undefined;

        return (
            <div>
                <ImportFilter
                    alterEditColumn={this.props.isPlanting || this.props.isHarvest}
                    isPlanting={this.props.isPlanting}
                    isHarvest={this.props.isHarvest}
                    filterTypeName={actions.ImportFilterTypes.CROPS}
                    title={formatMessage(messages.cropFilterTitle)}
                    itemProperties={this.filterProperties}
                    filterItems={this.props.filterItems}
                    additionalValidation={this.isItemCropLinked}
                    selectedFieldGuid={this.props.selectedFieldGuid}
                    selectItem={(item) => {
                        this.props.selectItem(item);
                    }}
                    onEditAttribute={(item) => {
                        this.onEditAttribute(item);
                    }}
                    onAddAlias={(item, index) => {
                        logFirebaseEvent("import_planting_variety_add_alias");
                        this.onAddAlias(item, index);
                    }}
                    updateFilterStatus={(isComplete) => this.props.updateFilterStatus(isComplete)}
                    removeImportFiles={(importFileGuidList) =>
                        this.props.removeImportFiles(importFileGuidList)
                    }
                    assignMatchedToUnmatched={(index) =>
                        this.props.assignMatchedToUnmatched(index, this.assignToCrops)
                    }
                />
                {!this.state.editItem ? null : (
                    <div>
                        <AttributeEditModal
                            className="crop-edit-modal"
                            title={formatMessage(messages.editCropText)}
                            isOpen={true}
                            canSave={this.canSaveEdit()}
                            onSaveEdit={this.onSaveEdit}
                            onCloseEdit={this.onCloseEdit}
                        >
                            {this.state.isSaving || this.state.isRefreshingLists ? (
                                <Loader />
                            ) : null}
                            <div className="cbv-inputs">
                                <SelectInput
                                    autoFocus
                                    openOnFocus={false}
                                    hasError={isSelectedCropUnlinked}
                                    optionIsHiddenKey={IS_SELECTABLE}
                                    value={this.state.cropGuid}
                                    options={this.state.options.crop}
                                    required={true}
                                    clearable={false}
                                    onChange={(value) => {
                                        this.updateCropModel("cropGuid", value);
                                    }}
                                    placeholderText={formatMessage(messages.cropPlaceholderText)}
                                    toolTip={cropNotLinkedText}
                                    testId="crop-filter-crop-select"
                                />
                                <CropPurposeDataHost
                                    cropGuid={this.state.cropGuid}
                                    formatMessage={formatMessage}
                                    onOptionsChange={this.onCropPurposeOptionsChange}
                                />
                                <SelectInput
                                    optionIsHiddenKey={IS_SELECTABLE}
                                    disabled={isCropPurposeSelectDisabled({
                                        cropGuid: this.state.cropGuid,
                                        options: this.state.options.cropPurpose,
                                        value: this.state.cropPurposeGuid,
                                    })}
                                    hasError={this.isSelectedCropPurposeUnlinked()}
                                    value={this.state.cropPurposeGuid}
                                    options={this.state.options.cropPurpose}
                                    onChange={(value) => {
                                        this.updateCropModel("cropPurposeGuid", value);
                                    }}
                                    placeholderText={formatMessage(
                                        messages.cropPurposePlaceholderText
                                    )}
                                    toolTip={
                                        this.isSelectedCropPurposeUnlinked()
                                            ? formatMessage(
                                                  globalMessages.cropPurposeNotLinkedInAgvance
                                              )
                                            : undefined
                                    }
                                    testId="crop-filter-crop-purpose-select"
                                />
                                <SelectInput
                                    optionIsHiddenKey={ACTIVE_YN}
                                    value={this.state.brandOrganizationGuid}
                                    options={this.state.options.brand}
                                    onChange={(value) => {
                                        this.updateCropModel("brandOrganizationGuid", value);
                                    }}
                                    placeholderText={formatMessage(messages.brandPlaceholderText)}
                                    disabled={
                                        !(
                                            this.state.options.brand.length > 0 &&
                                            this.state.cropGuid
                                        )
                                    }
                                    required={this.props.isPlanting}
                                    testId="crop-filter-brand-select"
                                />
                                <SelectInput
                                    optionIsHiddenKey={ACTIVE_YN}
                                    value={this.state.varietyHybridGuid}
                                    options={this.state.options.variety}
                                    onChange={(value) => {
                                        this.updateCropModel("varietyHybridGuid", value);
                                    }}
                                    placeholderText={formatMessage(messages.varietyPlaceholderText)}
                                    disabled={
                                        !(
                                            this.state.options.variety.length > 0 &&
                                            this.state.cropGuid
                                        )
                                    }
                                    required={this.props.isPlanting}
                                    testId="crop-filter-variety-select"
                                />
                                {!this.props.isPlanting ? null : (
                                    <NumericInput
                                        disabled={!this.state.isSeedingRateEnabled}
                                        scale={2}
                                        precision={9}
                                        value={formattedSeedingRate}
                                        required={false}
                                        onChange={(v) => {
                                            if (!this.state.isSeedingRateEnabled) {
                                                return;
                                            }
                                            this.setState({ userAdjustedSeedingRate: true });
                                            this.updateCropModelSeedingRate(v);
                                        }}
                                        placeholderText={formatMessage(messages.seedingRateText)}
                                        testId="crop-filter-seeding-rate-input"
                                    />
                                )}
                                {!this.props.isPlanting ? null : (
                                    <SelectInput
                                        optionIsHiddenKey={ACTIVE_YN}
                                        value={this.state.seedingRateIaGuid}
                                        options={this.props.seedingRateUnits?.map(
                                            ({ importAttributeGuid, abbreviation }) => ({
                                                value: importAttributeGuid,
                                                label: abbreviation,
                                            })
                                        )}
                                        required={true}
                                        clearable={false}
                                        onChange={(v) => {
                                            this.updateCropModelSeedingRateUnit(v);
                                            this.setState({ seedingRateUnitEdited: true });
                                        }}
                                        placeholderText={formatMessage(
                                            messages.seedingRateUnitText
                                        )}
                                        testId="crop-filter-seeding-rate-unit-select"
                                    />
                                )}
                                {this.props.isPlanting ? null : (
                                    <SelectInput
                                        optionIsHiddenKey={ACTIVE_YN}
                                        value={this.state.yieldIaGuid}
                                        options={(!ImportCropFilter_.isCottonCrop(this.state.cropId)
                                            ? this.state.options.yieldUnitDry
                                            : this.state.options.yieldUnitLint
                                        ).sort((a, b) => a.label.localeCompare(b.label))}
                                        required={true}
                                        clearable={false}
                                        onChange={(value) => {
                                            this.updateCropModel("yieldIaGuid", value);
                                        }}
                                        placeholderText={formatMessage(
                                            messages.yieldUnitPlaceholderText
                                        )}
                                        testId="crop-filter-yield-unit-select"
                                    />
                                )}
                                {this.props.isPlanting ||
                                !this.state.yieldIaGuid ||
                                !ImportCropFilter_.isVolumetricFlowRate(
                                    this.state.yieldUnitId
                                ) ? null : (
                                    <NumericInput
                                        scale={4}
                                        precision={9}
                                        value={this.state.averageUnitWeight}
                                        required={true}
                                        onChange={(v) => {
                                            this.updateCropModel("averageUnitWeight", v);
                                        }}
                                        placeholderText={formatMessage(
                                            messages.averageUnitWeightText
                                        )}
                                        testId="crop-filter-average-unit-weight-input"
                                    />
                                )}
                                {this.props.isPlanting ||
                                !this.state.yieldIaGuid ||
                                !ImportCropFilter_.isBushelPerAcre(
                                    this.state.yieldUnitId
                                ) ? null : (
                                    <NumericInput
                                        scale={4}
                                        precision={9}
                                        value={this.state.averageTestWeight}
                                        required={true}
                                        onChange={(v) => {
                                            this.updateCropModel("averageTestWeight", v);
                                        }}
                                        placeholderText={formatMessage(
                                            messages.averageTestWeightText
                                        )}
                                        testId="crop-filter-average-test-weight-input"
                                    />
                                )}
                                {this.props.isPlanting ||
                                !ImportCropFilter_.isCottonCrop(this.state.cropId) ? null : (
                                    <NumericInput
                                        scale={2}
                                        precision={5}
                                        value={this.state.lintPercent}
                                        required={true}
                                        onChange={(v) => {
                                            this.updateCropModel("lintPercent", v);
                                        }}
                                        placeholderText={formatMessage(
                                            messages.lintPercentPlaceholderText
                                        )}
                                        testId="crop-filter-lint-percent-input"
                                    />
                                )}
                            </div>
                            {this.props.isPlanting ? (
                                <div className="product-table">
                                    {this.buildProducts()}
                                    {!this.state.isProductBlendingActive ? null : (
                                        <ProductBlendModal
                                            customerGuid={this.props.customerGuid || undefined}
                                            productMix={
                                                this.state.originalProductMixList[
                                                    this.state.activeProductBlend
                                                ]
                                            }
                                            calculatedArea={1}
                                            isOpen={this.state.isProductBlendingActive}
                                            activeProductMixIdx={this.state.activeProductBlend}
                                            onUpdate={(productMix) =>
                                                this.onUpdateProductBlending(productMix)
                                            }
                                            onSave={() => this.onSaveProductBlending()}
                                            onCancel={() => this.onCancelProductBlending()}
                                        />
                                    )}
                                </div>
                            ) : null}
                        </AttributeEditModal>
                    </div>
                )}
                {!this.state.addAliasItem ? null : (
                    <div>
                        <AttributeEditModal
                            className="alias-add-modal"
                            title={formatMessage(messages.addAliasVarietyHybridText)}
                            isOpen={true}
                            canSave={this.canSaveAlias()}
                            onSaveEdit={this.onSaveAlias}
                            onCloseEdit={this.onCloseAlias}
                        >
                            {this.state.isSaving || this.state.isRefreshingLists ? (
                                <Loader />
                            ) : null}
                            <TextInput
                                maxLength={50}
                                value={this.state.aliasVarietyHybridName}
                                placeholderText={formatMessage(messages.aliasVarietyHybridNameText)}
                                onChange={(value) => {
                                    this.setState({
                                        aliasVarietyHybridName: value,
                                    });
                                }}
                                testId="crop-filter-alias-variety-name-input"
                            />
                        </AttributeEditModal>
                    </div>
                )}
                {this.props.isPlanting ? this.buildSeedingRateUnitModal() : null}
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    const dropdowns = selectors.getImportWizardDropdowns(state);
    const availableProducts = blendingSelectors.getAvailableProducts(state);
    const conversionFactors = blendingSelectors.getConversionFactors(state);
    const productBlendPicklists = blendingSelectors.getProductBlendPicklists(state);

    let dropDownData = {};

    for (const key of Object.keys(dropdowns)) {
        dropDownData = Object.assign(dropDownData, { [key]: dropdowns[key] });
    }

    return {
        ...dropDownData,
        isLoading: selectors.getIsLoading(state),
        userCropImportPreferences: selectors.getUserCropImportPreferences(state),
        availableProducts: availableProducts,
        conversionFactors: conversionFactors,
        productBlendPicklists: productBlendPicklists,
        seedingRateUnits: picklistSelectors.getPicklistFromCode(state, "seedingRate"),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        setIsLoading: (isLoading) => dispatch(actions.setIsLoading(isLoading)),
        fetchUserCropImportPreferences: (cropGuid, yieldIaGuid) =>
            dispatch(actions.fetchUserCropImportPreferences(cropGuid, yieldIaGuid)),
        setUserCropImportPreferences: (userCropImportPreferences) =>
            dispatch(actions.setUserCropImportPreferences(userCropImportPreferences)),
        refreshDropdowns: (dropdowns: Partial<DropdownRequest>[] | string[]) => {
            dispatch(
                fetchDropdownData({
                    ...dropdowns,
                    action: actions.setDropdownData,
                    async: false,
                })
            );
        },
        updateUserCropImportPreferences: (
            cropGuid,
            yieldIaGuid,
            averageTestWeight,
            averageUnitWeight
        ) => {
            dispatch(
                actions.updateUserCropImportPreferences(
                    cropGuid,
                    yieldIaGuid,
                    averageTestWeight,
                    averageUnitWeight
                )
            );
        },
    };
};

interface ICropPurposeDataHostProps {
    cropGuid: string;
    formatMessage: (descriptor: { id: string; defaultMessage: string }) => string;
    onOptionsChange: (options: ImportCropsFilterOption["cropPurpose"]) => void;
}

/**
 * Renders nothing -- hosts useCropPurposeList() for the edit modal's Purpose picker, since
 * ImportCropFilter_ is a class and can't call the hook directly. Scoped to whichever crop is
 * currently selected in the modal (this.state.cropGuid), so it only mounts while the modal is
 * open (see render()'s `!this.state.editItem ? null : ...` gate) and re-scopes whenever the
 * crop selection changes. Reports the stamped options up via onOptionsChange so the class's
 * render/canSaveEdit can read linkage state they have no other way to reach.
 */
const CropPurposeDataHost = ({
    cropGuid,
    formatMessage,
    onOptionsChange,
}: ICropPurposeDataHostProps) => {
    const cropScope = useImportCropScope();
    const { cropPurposes } = useCropPurposeList({ cropGuid, scope: cropScope });
    const options = useMemo(
        () => ImportCropFilter_.getCropPurposeOptionsFromList(cropPurposes, formatMessage),
        [cropPurposes, formatMessage]
    );

    useEffect(() => {
        onOptionsChange(options);
    }, [options]);

    return null;
};

const ConnectedImportCropFilter = connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(ImportCropFilter_));

export const ImportCropFilter = (props: Partial<ICropFilterProps>) => {
    const { crops } = useCropList({ scope: useImportCropScope() });

    return <ConnectedImportCropFilter {...props} {...{ [DROPDOWN_CROP]: crops }} />;
};
